import React, { useState, useEffect } from 'react';
import { Table, Card, Btn, Modal, Input, Select, Textarea, FormGrid, SectionHeader, Badge, StatCard, NeedProject, Tabs, RNLLoader } from '../components/UI';
import { fmt, fmtNum, statusColor, exportCSV } from '../utils';
import * as api from '../api';
import { priceRow } from '../utils/boqCosting';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import BOQFormPage from './BOQFormPage';

// The bar above the BOQ shows when onBack, contextLabel or barExtra is given (the Back button only with onBack);
// onDeleted runs after the BOQ is deleted.
export function BOQItemsTab({ projectId, boqId, onBack, toast, backLabel = 'Back to BOQ Forms', contextLabel, barExtra, onDeleted }) {
  const [boqSummary, setBoqSummary] = useState(null);
  const [subsections, setSubsections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [categories, setCategories] = useState([]);
  const [activeTab, setActiveTab] = useState('summary');
  const [editingItem, setEditingItem] = useState(null);
  const [addingItem, setAddingItem] = useState(false);
  const [newItemData, setNewItemData] = useState({
    boq_code: '', description: '', unit_of_measure: 'PCS', budgeted_quantity: 0,
    material_id: '', dmr_category_id: '', dmr_subsection_id: '',
    basic_rate_material: 0, factor: 1, unit_hour: 0, consultant_quantity: null, price_source_amount: 0
  });
  const [deletingItem, setDeletingItem] = useState(null);
  
  const [editingComponent, setEditingComponent] = useState(null);
  const [addingComponentToItem, setAddingComponentToItem] = useState(null);
  const [deletingComponent, setDeletingComponent] = useState(null);
  const [newComponentData, setNewComponentData] = useState({
    sub_component_code: '', description: '', unit_of_measure: 'PCS', budgeted_quantity: 0,
    material_id: '', dmr_category_id: '', dmr_subsection_id: '',
    basic_rate_material: 0, factor: 1, unit_hour: 0, consultant_quantity: null, price_source_amount: 0
  });
  const [isSaving, setIsSaving] = useState(false);
  const [materials, setMaterials] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  const [projectDivisions, setProjectDivisions] = useState([]);
  const [addingBuilding, setAddingBuilding] = useState(false);
  const [newBuildingId, setNewBuildingId] = useState('');
  const [removingBuilding, setRemovingBuilding] = useState(null);

  const toggleRow = (itemId) => {
    setExpandedRows(prev => ({ ...prev, [itemId]: !prev[itemId] }));
  };

  const DEFAULT_COLS = ['_sn', 'boq_code', 'description', 'material_id', 'unit_of_measure', 'budgeted_quantity'];
  const [visibleCols, setVisibleCols] = useState(() => {
    const saved = localStorage.getItem('boqVisibleCols');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return Array.from(new Set([...DEFAULT_COLS, ...parsed, 'actions']));
      } catch (e) { }
    }
    return [...DEFAULT_COLS, 'actions'];
  });
  const [showColSettings, setShowColSettings] = useState(false);

  useEffect(() => {
    localStorage.setItem('boqVisibleCols', JSON.stringify(visibleCols));
  }, [visibleCols]);

  const toggleCol = (key) => {
    if (DEFAULT_COLS.includes(key) || key === 'actions') return;
    setVisibleCols(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const AVAILABLE_COLS = [
    { key: 'boq_code', label: 'Item No.' },
    { key: 'description', label: 'Description' },
    { key: 'material_id', label: 'Material' },
    { key: 'unit_of_measure', label: 'Unit' },
    { key: 'budgeted_quantity', label: 'Qty' },
    { key: 'consultant_quantity', label: 'Cons. Qty' },
    { key: 'landed_cost_materials', label: 'Landed Cost' },
    { key: 'unit_hour', label: 'Unit Hr' },
    { key: 'price_source_amount', label: 'Price Source Amt' },
    { key: 'ex_works_pds_amount', label: 'Ex-Works PDS Amt' },
    { key: 'ex_works_euro_amount', label: 'Ex-Works Euro Amt' },
    { key: 'ex_works_usd_amount', label: 'Ex-Works USD Amt' },
    { key: 'fob_usd_amount', label: 'FOB USD Amt' },
    { key: 'sea_freight_amount', label: 'Sea Freight Amt' },
    { key: 'insurance_comsn_others_amount', label: 'Ins/Comsn Amt' },
    { key: 'cf_usd_amount', label: 'C&F USD Amt' },
    { key: 'cf_naira_amount', label: 'C&F Naira Amt' },
    { key: 'duty_surcharges_amount', label: 'Duty/Surch Amt' },
    { key: 'clearing_charges_amount', label: 'Clearing Amt' },
    { key: 'agency_other_charges_amount', label: 'Agency Charge Amt' },
    { key: 'installation_accessories_consumables', label: 'Install Acc.' },
    { key: 'transport_to_site', label: 'Transport' },
    { key: 'unit_rate_material', label: 'Unit Rate Mat.' },
    { key: 'total_material_delivered_site', label: 'Total Mat. Del.' },
    { key: 'equipment_and_tools', label: 'Equip & Tools' },
    { key: 'prelim_site_mgmt_supevission', label: 'Prelims' },
    { key: 'total_unit_rate_mat_lab', label: 'Tot Unit Rate (M+L)' },
    { key: 'total_amount_mat_lab', label: 'Tot Amt (M+L)' },
    { key: 'margin_markup_material_amount', label: 'Margin Mat.' },
    { key: 'margin_markup_labour_amount', label: 'Margin Lab.' },
    { key: 'basic_rate_material', label: 'Basic Rate Mat.' },
    { key: 'install_rate_labour', label: 'Install Rate Lab.' },
    { key: 'unit_rate', label: 'Unit Rate' },
    { key: 'amount', label: 'Total Amount' }
  ];

  const load = async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const [summaryRes, subsRes, catsRes, matRes, divRes] = await Promise.all([
        (boqId ? api.getBOQ(boqId) : api.getBOQSummaryForProject(projectId)).catch(() => null),
        api.listDmrSubsections(projectId).catch(() => []),
        api.listDmrCategories(projectId).catch(() => []),
        api.listMaterials().catch(() => []),
        api.listProjectDivisions(projectId).catch(() => []),
      ]);
      setBoqSummary(summaryRes);
      setSubsections(Array.isArray(subsRes) ? subsRes : (subsRes?.data || []));
      setCategories(Array.isArray(catsRes) ? catsRes : (catsRes?.data || []));
      setMaterials(Array.isArray(matRes) ? matRes : (matRes?.data || []));
      setProjectDivisions(Array.isArray(divRes) ? divRes : (divRes?.data || []));
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, [projectId, boqId]);

  if (loading) {
    return <RNLLoader title="Loading BOQ..." subtitle="Please wait" />;
  }

  if (isEditing) {
    return (
      <div className="fade-in" style={{ marginTop: 20 }}>
        <BOQFormPage
          project={{ project_id: projectId }}
          existingBoqData={boqSummary}
          onBack={() => { setIsEditing(false); load(); }}
          toast={toast}
        />
      </div>
    );
  }

  if (!boqSummary) {
    return (
      <div className="fade-in" style={{ marginTop: 20 }}>
        <Card style={{ padding: 60, textAlign: 'center', background: 'var(--bg-elevated)' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>📋</div>
          <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8, color: 'var(--text-primary)' }}>No BOQ Entered</h3>
          <p style={{ color: 'var(--text-muted)', marginBottom: 24, maxWidth: 400, margin: '0 auto 24px' }}>
            There is no Bill of Quantities associated with this project yet. Create one to start tracking budgets and expenses.
          </p>
          <Btn onClick={() => setIsEditing(true)}>Create BOQ Form</Btn>
        </Card>
      </div>
    );
  }

  const items = boqSummary.boq_items || [];

  const getSubName = id => {
    const sub = subsections.find(s => s.id === id);
    return sub ? sub.name : 'Unknown Subsection';
  };

  // The BOQ's tabs are its buildings/zones; a building tab shows that building's lines.
  const buildings = boqSummary.buildings || [];
  const buildingName = id => buildings.find(b => b.project_division_id === id)?.name
    || projectDivisions.find(d => (d.project_division_id || d.id) === id)?.name || 'Unassigned';
  const activeItems = activeTab === 'summary' ? items : items.filter(i => i.project_division_id === activeTab);

  // within a building, lines are grouped by category, then subsection
  const groups = {};
  activeItems.forEach(item => {
    const key = `${item.dmr_category_id || ''}|${item.dmr_subsection_id || ''}`;
    if (!groups[key]) groups[key] = [];
    groups[key].push(item);
  });
  const groupName = key => {
    const [catId, subId] = key.split('|');
    const cat = categories.find(c => c.id === catId)?.name;
    const sub = subId ? getSubName(subId) : null;
    return [cat, sub].filter(Boolean).join(' › ') || 'Uncategorised';
  };

  const sumAmount = rows => rows.reduce((acc, item) => acc + parseFloat(item.amount || 0), 0);
  // lines whose building is not one of the tabs (older data) still count in the summary
  const strayItems = items.filter(i => !buildings.some(b => b.project_division_id === i.project_division_id));
  const freeDivisions = projectDivisions.filter(d => !buildings.some(b => b.project_division_id === (d.project_division_id || d.id)));

  const handleAddBuilding = async () => {
    if (!newBuildingId) return;
    setIsSaving(true);
    try {
      await api.addBOQBuilding(boqSummary.id, newBuildingId);
      toast?.show?.('Building added', 'success');
      setAddingBuilding(false);
      setActiveTab(newBuildingId);
      setNewBuildingId('');
      load();
    } catch (e) {
      toast?.show?.(e.response?.data?.detail || 'Failed to add building', 'error');
    }
    setIsSaving(false);
  };

  const handleRemoveBuilding = async () => {
    setIsSaving(true);
    try {
      await api.removeBOQBuilding(boqSummary.id, removingBuilding);
      toast?.show?.('Building removed', 'success');
      setRemovingBuilding(null);
      setActiveTab('summary');
      load();
    } catch (e) {
      toast?.show?.(e.response?.data?.detail || 'Failed to remove building', 'error');
    }
    setIsSaving(false);
  };




  // The MEP bill template's pricing (src/utils/boqCosting.js — the same method as the BOQ workbook importer).
  // Inputs are QTY, Basic Rate, Factor and Unit Hour; the line's rates come from the line, else another line
  // of this BOQ, else the template's.
  const calculateRowFromSavedRates = (row) => Object.assign(row, priceRow(row, items));

  const handleCreateItem = async () => {
    setIsSaving(true);
    try {
      const computed = calculateRowFromSavedRates(newItemData);
      const payload = {
        ...newItemData,
        amount: computed.amount || 0,
        unit_rate: computed.unit_rate || 0,
        basic_rate_material: computed.basic_rate_material || 0,
        install_rate_labour: computed.install_rate_labour || 0,
        dmr_category_id: newItemData.dmr_category_id || null,
        dmr_subsection_id: newItemData.dmr_subsection_id || null,
        project_division_id: newItemData.project_division_id || null,
        material_id: newItemData.material_id || null
      };
      if (!payload.project_division_id) {
        toast?.show?.('Choose the building this line belongs to', 'error');
        setIsSaving(false);
        return;
      }
      await api.addBOQItem(boqSummary.id, payload);
      toast?.show?.('BOQ item created successfully', 'success');
      setAddingItem(false);
      load(); // Reload the whole BOQ to see changes
    } catch (e) {
      toast?.show?.(e.response?.data?.detail || 'Failed to create BOQ item', 'error');
    }
    setIsSaving(false);
  };

  const handleSaveItem = async () => {
    setIsSaving(true);
    try {
      const computedItem = calculateRowFromSavedRates({ ...editingItem });
      const payload = {
        ...computedItem,
        dmr_category_id: computedItem.dmr_category_id || null,
        dmr_subsection_id: computedItem.dmr_subsection_id || null,
        project_division_id: computedItem.project_division_id || null,
        material_id: computedItem.material_id || null
      };
      await api.updateBOQItem(computedItem.boq_item_id || computedItem.id, payload);
      toast?.show?.('BOQ item updated successfully', 'success');
      setEditingItem(null);
      load(); // Reload the whole BOQ to see changes
    } catch (e) {
      toast?.show?.('Failed to update BOQ item', 'error');
    }
    setIsSaving(false);
  };

  const handleDeleteItem = async () => {
    setIsSaving(true);
    try {
      await api.deleteBOQItem(deletingItem.boq_item_id || deletingItem.id);
      toast?.show?.('BOQ item deleted successfully', 'success');
      setDeletingItem(null);
      load();
    } catch (e) {
      toast?.show?.('Failed to delete BOQ item', 'error');
    }
    setIsSaving(false);
  };

  const handleCreateComponent = async () => {
    setIsSaving(true);
    try {
      const computed = calculateRowFromSavedRates(newComponentData);
      const payload = {
        ...newComponentData,
        amount: computed.amount || 0,
        unit_rate: computed.unit_rate || 0,
        basic_rate_material: computed.basic_rate_material || 0,
        install_rate_labour: computed.install_rate_labour || 0,
        dmr_category_id: newComponentData.dmr_category_id || null,
        dmr_subsection_id: newComponentData.dmr_subsection_id || null,
        material_id: newComponentData.material_id || null
      };
      await api.addBOQComponent(addingComponentToItem.boq_item_id || addingComponentToItem.id, payload);
      toast?.show?.('Component created successfully', 'success');
      setAddingComponentToItem(null);
      setNewComponentData({
        sub_component_code: '', description: '', unit_of_measure: 'PCS', budgeted_quantity: 0,
        material_id: '', dmr_category_id: '', dmr_subsection_id: '',
        basic_rate_material: 0, factor: 1, unit_hour: 0, consultant_quantity: null, price_source_amount: 0
      });
      load();
    } catch (e) {
      toast?.show?.(e.response?.data?.detail || 'Failed to create component', 'error');
    }
    setIsSaving(false);
  };

  const handleSaveComponent = async () => {
    setIsSaving(true);
    try {
      const computedComponent = calculateRowFromSavedRates({ ...editingComponent });
      const payload = {
        ...computedComponent,
        dmr_category_id: computedComponent.dmr_category_id || null,
        dmr_subsection_id: computedComponent.dmr_subsection_id || null,
        material_id: computedComponent.material_id || null
      };
      await api.updateBOQComponent(computedComponent.component_id || computedComponent.id, payload);
      toast?.show?.('Component updated successfully', 'success');
      setEditingComponent(null);
      load();
    } catch (e) {
      toast?.show?.('Failed to update component', 'error');
    }
    setIsSaving(false);
  };

  const handleDeleteComponent = async () => {
    setIsSaving(true);
    try {
      await api.deleteBOQComponent(deletingComponent.component_id || deletingComponent.id);
      toast?.show?.('Component deleted successfully', 'success');
      setDeletingComponent(null);
      load();
    } catch (e) {
      toast?.show?.('Failed to delete component', 'error');
    }
    setIsSaving(false);
  };

  const getCatName = id => {
    const cat = categories.find(c => c.id === id);
    return cat ? cat.name : 'Uncategorized';
  };


  const handleDeleteBoq = async () => {
    setIsDeleting(true);
    try {
      await api.deleteBOQ(boqSummary.id);
      toast.show("BOQ deleted successfully", "success");
      setBoqSummary(null);
      setShowDeleteModal(false);
      onDeleted?.();
    } catch (e) {
      toast.show(e.message || "Failed to delete BOQ", "error");
    } finally {
      setIsDeleting(false);
    }
  };



  return (
    <div className="fade-in" style={{ marginTop: 20 }}>
      {(onBack || contextLabel || barExtra) && (
        <>
          <style>{'#global-project-back-bar { display: none !important; }'}</style>
          <div className="flex-mobile-col" style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: 'var(--bg-card)', border: '1px solid black', borderRadius: 14, boxShadow: 'var(--shadow-sm)', backgroundColor: '#e4ebefdf' }}>
            {onBack && (
            <button
              onClick={onBack}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                background: 'var(--accent-dim)', color: 'var(--accent)', border: '1px solid color-mix(in srgb, var(--accent) 22%, transparent)', borderRadius: 10,
                padding: '7px 13px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font)'
              }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></svg>
              {backLabel}
            </button>
            )}
            <span style={{ fontSize: 13, color: 'var(--text-secondary)', flex: 1 }}>
              {contextLabel || <>Currently managing BOQ: <strong style={{ color: 'var(--text-primary)' }}>{boqSummary.name}</strong></>}
            </span>
            {barExtra}
          </div>
        </>
      )}
      <div style={{ border: '1px solid black', padding: 16, borderRadius: 18, background: 'var(--bg-surface)' }}>
      {/* Header Summary Card */}
      <Card style={{ padding: 24, marginBottom: 24, border: '2px solid black', background: '#0b318bff', color: '#fff', borderRadius: 18 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <h2 style={{ margin: '0 0 8px 0', fontSize: 24, fontWeight: 800 }}>{boqSummary.name}</h2>
            <div style={{ fontSize: 14, opacity: 0.8 }}>{boqSummary.description}</div>
            {boqSummary.attention && <div style={{ fontSize: 11, marginTop: 4, opacity: 0.7 }}>Client Name: {boqSummary.attention}</div>}
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <button
              onClick={() => {
                setNewItemData({
                  boq_code: '', description: '', unit_of_measure: 'PCS', budgeted_quantity: 0,
                  project_division_id: activeTab !== 'summary' ? activeTab : (buildings.length === 1 ? buildings[0].project_division_id : ''),
                  material_id: '', dmr_category_id: '', dmr_subsection_id: '',
                  basic_rate_material: 0, factor: 1, unit_hour: 0, consultant_quantity: null, price_source_amount: 0
                });
                setAddingItem(true);
              }}
              style={{ background: 'rgba(16, 185, 129, 0.15)', color: '#34d399', border: '1px solid rgba(16, 185, 129, 0.3)', padding: '8px', borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease' }}
              onMouseEnter={e => { e.currentTarget.style.background = '#10b981'; e.currentTarget.style.color = '#fff'; e.currentTarget.style.transform = 'scale(1.05)'; e.currentTarget.style.boxShadow = '0 0 12px rgba(16, 185, 129, 0.5)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(16, 185, 129, 0.15)'; e.currentTarget.style.color = '#34d399'; e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = 'none'; }}
              title="Add New Line Item"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="16"></line><line x1="8" y1="12" x2="16" y2="12"></line></svg>
            </button>
            <button
              onClick={() => setIsEditing(true)}
              style={{ background: 'rgba(245, 158, 11, 0.15)', color: '#fbbf24', border: '1px solid rgba(245, 158, 11, 0.3)', padding: '8px', borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease' }}
              onMouseEnter={e => { e.currentTarget.style.background = '#f59e0b'; e.currentTarget.style.color = '#fff'; e.currentTarget.style.transform = 'scale(1.05)'; e.currentTarget.style.boxShadow = '0 0 12px rgba(245, 158, 11, 0.5)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(245, 158, 11, 0.15)'; e.currentTarget.style.color = '#fbbf24'; e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = 'none'; }}
              title="Edit BOQ Form"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
            </button>
            <button
              onClick={() => setShowDeleteModal(true)}
              style={{ background: 'rgba(239, 68, 68, 0.15)', color: '#fca5a5', border: '1px solid rgba(239, 68, 68, 0.3)', padding: '8px', borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease' }}
              onMouseEnter={e => { e.currentTarget.style.background = '#ef4444'; e.currentTarget.style.color = '#fff'; e.currentTarget.style.transform = 'scale(1.05)'; e.currentTarget.style.boxShadow = '0 0 12px rgba(239, 68, 68, 0.5)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(239, 68, 68, 0.15)'; e.currentTarget.style.color = '#fca5a5'; e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = 'none'; }}
              title="Delete BOQ Form"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2-2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>
            </button>
          </div>
        </div>

        <div className="grid-cols-4" style={{ gap: 16 }}>
          <div>
            <div style={{ fontSize: 11, opacity: 0.7, fontWeight: 700, letterSpacing: '0.05em', marginBottom: 4 }}>ENTRY DATE</div>
            <div style={{ fontSize: 16, fontWeight: 600 }}>{boqSummary.entry_date}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, opacity: 0.7, fontWeight: 700, letterSpacing: '0.05em', marginBottom: 4 }}>TOTAL ITEMS</div>
            <div style={{ fontSize: 16, fontWeight: 600 }}>{items.length}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, opacity: 0.7, fontWeight: 700, letterSpacing: '0.05em', marginBottom: 4 }}>VAT RATE</div>
            <div style={{ fontSize: 16, fontWeight: 600 }}>{(boqSummary.vat_rate * 100).toFixed(1)}%</div>
          </div>
          <div>
            <div style={{ fontSize: 11, opacity: 0.7, fontWeight: 700, letterSpacing: '0.05em', marginBottom: 4 }}>GRAND TOTAL</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#22d3ee' }}>₦{parseFloat(boqSummary.grand_total || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          </div>
        </div>
      </Card>

      {/* Building / zone tabs */}
      <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 auto', minWidth: 0 }}>
          <Tabs
            tabs={[
              { id: 'summary', label: 'GENERAL SUMMARY', icon: '📋' },
              ...buildings.map(b => ({ id: b.project_division_id, label: b.name || 'Building', icon: '🏢' }))
            ]}
            active={activeTab}
            onChange={setActiveTab}
          />
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {activeTab !== 'summary' && (
            <Btn variant="ghost" size="sm" onClick={() => setRemovingBuilding(activeTab)}>Remove Building</Btn>
          )}
          <Btn variant="secondary" size="sm" onClick={() => { setNewBuildingId(''); setAddingBuilding(true); }}>+ Add Building</Btn>
        </div>
      </div>

      {activeTab === 'summary' ? (
        <Card style={{ marginBottom: 0, border: '1px solid black', overflow: 'hidden' }}>
          <div style={{ padding: '12px 16px', background: 'var(--bg-elevated)', borderBottom: '1px solid var(--border)' }}>
            <h4 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>GENERAL SUMMARY — {boqSummary.name}</h4>
          </div>
          <Table cols={[
            { key: 'sn', align: 'center', label: 'ITEM', render: (_, __, i) => <span style={{ fontWeight: 600 }}>{i + 1}</span> },
            { key: 'description', align: 'center', label: 'BUILDING / ZONE', render: (v, row) => row.id
              ? <button onClick={() => setActiveTab(row.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, color: 'var(--accent)' }}>{v}</button>
              : <span style={{ fontWeight: 500 }}>{v}</span> },
            { key: 'lines', align: 'center', label: 'LINES' },
            { key: 'amount', label: 'AMOUNT (NAIRA ₦)', align: 'right', render: v => <span style={{ fontFamily: 'var(--mono)', fontWeight: 700 }}>₦ {fmtNum(v)}</span> }
          ]} rows={[
            ...buildings.map(b => {
              const bItems = items.filter(i => i.project_division_id === b.project_division_id);
              return { id: b.project_division_id, sn: '', description: b.name || 'Building', lines: bItems.length, amount: sumAmount(bItems) };
            }),
            ...(strayItems.length ? [{ sn: '', description: 'Lines without a building', lines: strayItems.length, amount: sumAmount(strayItems) }] : []),
          ]} />

          <div style={{ background: '#f8fafc', padding: '12px 16px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontWeight: 700, color: '#334155' }}>SUB TOTAL AS PER BOQ [NAIRA]</span>
            <span style={{ fontFamily: 'var(--mono)', fontWeight: 800, color: '#0f172a' }}>₦ {fmtNum(items.reduce((acc, item) => acc + parseFloat(item.amount || 0), 0))}</span>
          </div>
          <div style={{ background: '#fefce8', padding: '12px 16px', borderTop: '1px solid #fde047', display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontWeight: 600, color: '#854d0e' }}>Add VAT @ {(boqSummary.vat_rate * 100).toFixed(1)}%</span>
            <span style={{ fontFamily: 'var(--mono)', fontWeight: 700, color: '#a16207' }}>₦ {fmtNum(items.reduce((acc, item) => acc + parseFloat(item.amount || 0), 0) * boqSummary.vat_rate)}</span>
          </div>
          <div style={{ background: '#0b318bff', padding: '16px', display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontWeight: 800, color: '#ffffff', fontSize: 16 }}>GRAND TOTAL [NAIRA]</span>
            <span style={{ fontFamily: 'var(--mono)', fontWeight: 800, color: '#ffffff', fontSize: 18 }}>₦ {fmtNum(boqSummary.grand_total)}</span>
          </div>
        </Card>
      ) : (
        <>
          <Card style={{ marginBottom: 0, border: '1px solid black', overflow: 'hidden', borderRadius: 8 }}>
            <div style={{ padding: '8px 12px', background: 'var(--bg-elevated)', borderBottom: '1px solid var(--border)' }}>
              <button onClick={() => setShowColSettings(true)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#0b318bff', display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, fontSize: 13 }} title="Choose Visible Columns">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path></svg>
                Column Settings
              </button>
            </div>
            <Table cols={[
              { key: '_sn', align: 'center', label: 'S/N', stickyLeft: 0, width: 50, render: v => <span style={{ fontFamily: 'var(--mono)', fontSize: 11.5, color: 'var(--text-secondary)' }}>{v}</span> },
              { key: 'boq_code', align: 'center', label: 'Item No.', stickyLeft: 50, width: 90, render: v => <span style={{ fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 600, color: 'var(--accent)', display: 'inline-block' }}>{v}</span> },
              { key: 'description', align: 'center', label: 'Description', stickyLeft: 140, width: 300, render: v => <span style={{ minWidth: 200, maxWidth: 300, display: 'block', whiteSpace: 'normal', fontSize: 11.5 }}>{v}</span> },
              { key: 'unit_of_measure', align: 'center', label: 'Unit', stickyLeft: 440, width: 80 },
              { key: 'budgeted_quantity', align: 'center', label: 'Qty', stickyLeft: 520, width: 80, isLastSticky: true, render: v => fmtNum(v) },
              { key: 'material_id', align: 'center', label: 'Material', width: 140, render: (_, row) => <span style={{ minWidth: 100, maxWidth: 140, display: 'block', whiteSpace: 'normal', fontSize: 11.5 }}>{materials.find(m => (m.material_id || m.id) === row.material_id)?.name || '—'}</span> },
              { key: 'consultant_quantity', align: 'center', label: 'Cons. Qty', render: v => (v == null ? '—' : fmtNum(v)) },
              { key: 'landed_cost_materials', align: 'center', label: 'Landed Cost', render: v => <span style={{ fontFamily: 'var(--mono)', fontSize: 11.5 }}>{fmt(v)}</span> },
              { key: 'unit_hour', align: 'center', label: 'Unit Hr', render: v => <span style={{ fontFamily: 'var(--mono)', fontSize: 11.5 }}>{fmt(v)}</span> },
              { key: 'price_source_amount', align: 'center', label: 'Price Source Amt', render: v => <span style={{ fontFamily: 'var(--mono)', fontSize: 11.5 }}>{fmt(v)}</span> },
              { key: 'ex_works_pds_amount', align: 'center', label: 'Ex-Works PDS Amt', render: v => <span style={{ fontFamily: 'var(--mono)', fontSize: 11.5 }}>{fmt(v)}</span> },
              { key: 'ex_works_euro_amount', align: 'center', label: 'Ex-Works Euro Amt', render: v => <span style={{ fontFamily: 'var(--mono)', fontSize: 11.5 }}>{fmt(v)}</span> },
              { key: 'ex_works_usd_amount', align: 'center', label: 'Ex-Works USD Amt', render: v => <span style={{ fontFamily: 'var(--mono)', fontSize: 11.5 }}>{fmt(v)}</span> },
              { key: 'fob_usd_amount', align: 'center', label: 'FOB USD Amt', render: v => <span style={{ fontFamily: 'var(--mono)', fontSize: 11.5 }}>{fmt(v)}</span> },
              { key: 'sea_freight_amount', align: 'center', label: 'Sea Freight Amt', render: v => <span style={{ fontFamily: 'var(--mono)', fontSize: 11.5 }}>{fmt(v)}</span> },
              { key: 'insurance_comsn_others_amount', align: 'center', label: 'Ins/Comsn Amt', render: v => <span style={{ fontFamily: 'var(--mono)', fontSize: 11.5 }}>{fmt(v)}</span> },
              { key: 'cf_usd_amount', align: 'center', label: 'C&F USD Amt', render: v => <span style={{ fontFamily: 'var(--mono)', fontSize: 11.5 }}>{fmt(v)}</span> },
              { key: 'cf_naira_amount', align: 'center', label: 'C&F Naira Amt', render: v => <span style={{ fontFamily: 'var(--mono)', fontSize: 11.5 }}>{fmt(v)}</span> },
              { key: 'duty_surcharges_amount', align: 'center', label: 'Duty/Surch Amt', render: v => <span style={{ fontFamily: 'var(--mono)', fontSize: 11.5 }}>{fmt(v)}</span> },
              { key: 'clearing_charges_amount', align: 'center', label: 'Clearing Amt', render: v => <span style={{ fontFamily: 'var(--mono)', fontSize: 11.5 }}>{fmt(v)}</span> },
              { key: 'agency_other_charges_amount', align: 'center', label: 'Agency Charge Amt', render: v => <span style={{ fontFamily: 'var(--mono)', fontSize: 11.5 }}>{fmt(v)}</span> },
              { key: 'installation_accessories_consumables', align: 'center', label: 'Install Acc.', render: v => <span style={{ fontFamily: 'var(--mono)', fontSize: 11.5 }}>{fmt(v)}</span> },
              { key: 'transport_to_site', align: 'center', label: 'Transport', render: v => <span style={{ fontFamily: 'var(--mono)', fontSize: 11.5 }}>{fmt(v)}</span> },
              { key: 'unit_rate_material', align: 'center', label: 'Unit Rate Mat.', render: v => <span style={{ fontFamily: 'var(--mono)', fontSize: 11.5 }}>{fmt(v)}</span> },
              { key: 'total_material_delivered_site', align: 'center', label: 'Total Mat. Del.', render: v => <span style={{ fontFamily: 'var(--mono)', fontSize: 11.5 }}>{fmt(v)}</span> },
              { key: 'equipment_and_tools', align: 'center', label: 'Equip & Tools', render: v => <span style={{ fontFamily: 'var(--mono)', fontSize: 11.5 }}>{fmt(v)}</span> },
              { key: 'prelim_site_mgmt_supevission', align: 'center', label: 'Prelims', render: v => <span style={{ fontFamily: 'var(--mono)', fontSize: 11.5 }}>{fmt(v)}</span> },
              { key: 'total_unit_rate_mat_lab', align: 'center', label: 'Tot Unit Rate (M+L)', render: v => <span style={{ fontFamily: 'var(--mono)', fontSize: 11.5 }}>{fmt(v)}</span> },
              { key: 'total_amount_mat_lab', align: 'center', label: 'Tot Amt (M+L)', render: v => <span style={{ fontFamily: 'var(--mono)', fontSize: 11.5 }}>{fmt(v)}</span> },
              { key: 'margin_markup_material_amount', align: 'center', label: 'Margin Mat.', render: v => <span style={{ fontFamily: 'var(--mono)', fontSize: 11.5 }}>{fmt(v)}</span> },
              { key: 'margin_markup_labour_amount', align: 'center', label: 'Margin Lab.', render: v => <span style={{ fontFamily: 'var(--mono)', fontSize: 11.5 }}>{fmt(v)}</span> },
              { key: 'basic_rate_material', align: 'center', label: 'Basic Rate Mat.', render: v => <span style={{ fontFamily: 'var(--mono)', fontSize: 11.5, color: 'var(--accent)', fontWeight: 600 }}>{fmt(v)}</span> },
              { key: 'install_rate_labour', align: 'center', label: 'Install Rate Lab.', render: v => <span style={{ fontFamily: 'var(--mono)', fontSize: 11.5, color: 'var(--accent)', fontWeight: 600 }}>{fmt(v)}</span> },
              { key: 'unit_rate', align: 'center', label: 'Unit Rate', render: v => <span style={{ fontFamily: 'var(--mono)', fontSize: 11.5, color: 'var(--accent)', fontWeight: 600 }}>{fmt(v)}</span> },
              { key: 'amount', align: 'center', label: 'Total Amount', render: v => <span style={{ fontFamily: 'var(--mono)', fontWeight: 800, color: '#0b318bff' }}>{fmt(v)}</span> },
              {
                key: 'actions', label: '', align: 'right', render: (_, item) => {
                  if (item._isComponent) return (
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => setEditingComponent(item)}
                        style={{
                          width: 32, height: 32, borderRadius: 8, border: '1px solid rgba(11, 49, 139, 0.2)', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          background: 'rgba(11, 49, 139, 0.1)', color: '#0b318bff',
                          transition: 'all 0.2s'
                        }}
                        onMouseOver={e => e.currentTarget.style.background = 'rgba(11, 49, 139, 0.2)'}
                        onMouseOut={e => e.currentTarget.style.background = 'rgba(11, 49, 139, 0.1)'}
                        title="Edit Component"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => setDeletingComponent(item)}
                        style={{
                          width: 32, height: 32, borderRadius: 8, border: '1px solid rgba(239, 68, 68, 0.2)', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444',
                          transition: 'all 0.2s'
                        }}
                        onMouseOver={e => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)'}
                        onMouseOut={e => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'}
                        title="Delete Component"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                          <line x1="10" y1="11" x2="10" y2="17" />
                          <line x1="14" y1="11" x2="14" y2="17" />
                        </svg>
                      </button>
                    </div>
                  );
                  return (
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    {(item.has_components || (item.components && item.components.length > 0)) && (
                      <button
                        onClick={() => toggleRow(item.boq_item_id || item.id)}
                        style={{
                          width: 32, height: 32, borderRadius: 8, border: 'none', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          background: expandedRows[item.boq_item_id || item.id] ? '#0b318bff' : '#e0e7ff',
                          color: expandedRows[item.boq_item_id || item.id] ? '#ffffff' : '#0b318bff',
                          border: expandedRows[item.boq_item_id || item.id] ? '1px solid #0b318bff' : '1px solid rgba(11, 49, 139, 0.2)',
                          transition: 'all 0.2s',
                          boxShadow: expandedRows[item.boq_item_id || item.id] ? '0 4px 6px -1px rgba(11, 49, 139, 0.3)' : 'none'
                        }}
                        title={expandedRows[item.boq_item_id || item.id] ? "Collapse Components" : "Expand Components"}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transform: expandedRows[item.boq_item_id || item.id] ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                          <polyline points="9 18 15 12 9 6" />
                        </svg>
                      </button>
                    )}
                    <button
                      onClick={() => setAddingComponentToItem(item)}
                      style={{
                        width: 32, height: 32, borderRadius: 8, border: '1px solid rgba(16, 185, 129, 0.2)', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: 'rgba(16, 185, 129, 0.1)', color: '#10b981',
                        transition: 'all 0.2s'
                      }}
                      onMouseOver={e => e.currentTarget.style.background = 'rgba(16, 185, 129, 0.2)'}
                      onMouseOut={e => e.currentTarget.style.background = 'rgba(16, 185, 129, 0.1)'}
                      title="Add Component"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                      </svg>
                    </button>
                    <button
                      onClick={() => setEditingItem(item)}
                      style={{
                        width: 32, height: 32, borderRadius: 8, border: '1px solid rgba(11, 49, 139, 0.2)', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: 'rgba(11, 49, 139, 0.1)', color: '#0b318bff',
                        transition: 'all 0.2s'
                      }}
                      onMouseOver={e => e.currentTarget.style.background = 'rgba(11, 49, 139, 0.2)'}
                      onMouseOut={e => e.currentTarget.style.background = 'rgba(11, 49, 139, 0.1)'}
                      title="Edit"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => setDeletingItem(item)}
                      style={{
                        width: 32, height: 32, borderRadius: 8, border: '1px solid rgba(220, 38, 38, 0.2)', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: 'rgba(220, 38, 38, 0.1)', color: '#dc2626',
                        transition: 'all 0.2s'
                      }}
                      onMouseOver={e => e.currentTarget.style.background = 'rgba(220, 38, 38, 0.2)'}
                      onMouseOut={e => e.currentTarget.style.background = 'rgba(220, 38, 38, 0.1)'}
                      title="Delete"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        <line x1="10" y1="11" x2="10" y2="17"></line>
                        <line x1="14" y1="11" x2="14" y2="17"></line>
                      </svg>
                    </button>
                  </div>
                  );
                }
              }
            ].filter(c => visibleCols.includes(c.key))} rows={
              Object.keys(groups).flatMap((subId, idx) => {
                const subItems = groups[subId];
                const subTotal = subItems.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
                return [
                  {
                    _isGroupHeader: true,
                    id: `group-${subId || 'none'}`,
                    content: (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, position: 'sticky', left: 16 }}>
                          <h4 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{groupName(subId)}</h4>
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', paddingRight: 16, position: 'sticky', right: 16 }}>₦{subTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                      </div>
                    )
                  },
                  ...subItems.flatMap((item, index) => {
                    const rows = [{ ...item, _sn: index + 1 }];
                    if ((item.has_components || (item.components && item.components.length > 0)) && expandedRows[item.boq_item_id || item.id]) {
                      (item.components || []).forEach((comp, cIdx) => {
                        rows.push({
                           ...comp,
                           _sn: '',
                           boq_code: `↳ ${comp.sub_component_code || ''}`,
                           _isComponent: true,
                           _bgColor: '#fdf2f8',
                           _hoverBgColor: '#fce7f3',
                           _fontStyle: 'italic'
                        });
                      });
                    }
                    return rows;
                  })
                ];
              })
            } />
          </Card>
        </>
      )}

      </div>

      {/* Delete Item Modal */}
      {deletingItem && (
        <Modal title="Delete Line Item" onClose={() => setDeletingItem(null)} headerBg="#0b318bff" headerColor="#ffffff">
          <p>Are you sure you want to delete this line item?</p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 24 }}>
            <Btn variant="ghost" onClick={() => setDeletingItem(null)}>Cancel</Btn>
            <Btn variant="danger" onClick={handleDeleteItem} disabled={isSaving}>
              {isSaving ? 'Deleting...' : 'Delete'}
            </Btn>
          </div>
        </Modal>
      )}

      {/* Column Settings Modal */}
      {showColSettings && (
        <Modal title="Table Columns" onClose={() => setShowColSettings(false)} headerBg="#0b318b" headerColor="#ffffff" width={500}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px' }}>
            {AVAILABLE_COLS.map(c => {
              const isDefault = DEFAULT_COLS.includes(c.key);
              const isChecked = visibleCols.includes(c.key);
              return (
                <label key={c.key} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: isDefault ? 'not-allowed' : 'pointer', opacity: isDefault ? 0.7 : 1 }}>
                  <input
                    type="checkbox"
                    checked={isChecked}
                    disabled={isDefault}
                    onChange={() => toggleCol(c.key)}
                    style={{ width: 16, height: 16, accentColor: '#0b318b' }}
                  />
                  <span style={{ fontSize: 13, color: 'var(--text-primary)' }}>{c.label}</span>
                </label>
              );
            })}
          </div>
          <div style={{ marginTop: 24, padding: '12px', background: '#f8fafc', borderRadius: 8, fontSize: 12, color: '#64748b' }}>
            Settings are automatically saved to your browser.
          </div>
        </Modal>
      )}

      {/* Create Item Modal */}
      {addingItem && (
        <Modal title="Create Line Item" onClose={() => setAddingItem(false)} headerBg="#0b318b" headerColor="#ffffff" width={800}>
          <FormGrid cols={2}>
            <div style={{ gridColumn: 'span 2' }}>
              <label>Building / Zone</label>
              <Select
                value={newItemData.project_division_id || ''}
                onChange={v => setNewItemData({ ...newItemData, project_division_id: v })}
                options={buildings.map(b => ({ value: b.project_division_id, label: b.name || 'Building' }))}
              />
            </div>
            <div>
              <label>Category</label>
              <Select
                value={newItemData.dmr_category_id || ''}
                onChange={v => setNewItemData({ ...newItemData, dmr_category_id: v })}
                options={categories.map(c => ({ value: c.id, label: c.name }))}
              />
            </div>
            <div>
              <label>Subsection</label>
              <Select
                value={newItemData.dmr_subsection_id || ''}
                onChange={v => setNewItemData({ ...newItemData, dmr_subsection_id: v })}
                options={subsections.map(s => ({ value: s.id, label: s.name }))}
              />
            </div>
            <div>
              <label>Material</label>
              <Select
                value={newItemData.material_id || ''}
                onChange={v => setNewItemData({ ...newItemData, material_id: v })}
                options={materials.map(m => ({ value: m.material_id || m.id, label: m.name }))}
              />
            </div>
            <div>
              <label>BOQ Code</label>
              <Input
                value={newItemData.boq_code || ''}
                onChange={v => setNewItemData({ ...newItemData, boq_code: v })}
              />
            </div>
            <div style={{ gridColumn: 'span 2' }}>
              <label>Description</label>
              <Textarea
                value={newItemData.description || ''}
                onChange={v => setNewItemData({ ...newItemData, description: v })}
                rows={3}
              />
            </div>
            <div>
              <label>Unit of Measure</label>
              <Select
                value={newItemData.unit_of_measure || ''}
                onChange={v => setNewItemData({ ...newItemData, unit_of_measure: v })}
                options={[
                  { value: 'PCS', label: 'PCS' },
                  { value: 'MT', label: 'MT' },
                  { value: 'KG', label: 'KG' },
                  { value: 'LS', label: 'LS' },
                  { value: 'Lot', label: 'Lot' },
                  { value: 'M', label: 'M' }
                ]}
              />
            </div>
            <div>
              <label>Budgeted Quantity</label>
              <Input
                type="number"
                value={newItemData.budgeted_quantity || 0}
                onChange={v => setNewItemData({ ...newItemData, budgeted_quantity: v })}
              />
            </div>
            <div>
              <label>Consultant Quantity</label>
              <Input
                type="number"
                value={newItemData.consultant_quantity ?? ''}
                placeholder="Same as budgeted"
                onChange={v => setNewItemData({ ...newItemData, consultant_quantity: v === '' ? null : v })}
              />
            </div>
            <div>
              <label>Basic Rate (Materials)</label>
              <Input
                type="number"
                value={newItemData.basic_rate_material || 0}
                onChange={v => setNewItemData({ ...newItemData, basic_rate_material: v })}
              />
            </div>
            <div>
              <label>Factor</label>
              <Input
                type="number"
                value={newItemData.factor || 0}
                onChange={v => setNewItemData({ ...newItemData, factor: v })}
              />
            </div>
            <div>
              <label>Unit Hour (Labour)</label>
              <Input
                type="number"
                value={newItemData.unit_hour || 0}
                onChange={v => setNewItemData({ ...newItemData, unit_hour: v })}
              />
            </div>
            <div>
              <label>Price Source Amount</label>
              <Input
                type="number"
                value={newItemData.price_source_amount || 0}
                onChange={v => setNewItemData({ ...newItemData, price_source_amount: v })}
              />
            </div>
          </FormGrid>

          <div style={{ marginTop: 24, padding: '16px', background: '#f8fafc', borderRadius: 12, fontSize: 11.5, color: '#64748b' }}>
            Note: The labour rate, landed cost, unit rate and amount are worked out from QTY, Basic Rate, Factor and Unit Hour with this BOQ’s pricing rates — the same method as the Excel BOQ template.
          </div>

          <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
            <Btn variant="ghost" onClick={() => setAddingItem(false)}>Cancel</Btn>
            <Btn onClick={handleCreateItem} disabled={isSaving || !newItemData.boq_code || !newItemData.description}>
              {isSaving ? 'Creating...' : 'Create Item'}
            </Btn>
          </div>
        </Modal>
      )}

      {/* Edit Item Modal */}
      {editingItem && (
        <Modal title="Edit Line Item" onClose={() => setEditingItem(null)} headerBg="#0b318b" headerColor="#ffffff" width={800}>
          <FormGrid cols={2}>
            <div style={{ gridColumn: 'span 2' }}>
              <label>Building / Zone</label>
              <Select
                value={editingItem.project_division_id || ''}
                onChange={v => setEditingItem({ ...editingItem, project_division_id: v })}
                options={buildings.map(b => ({ value: b.project_division_id, label: b.name || 'Building' }))}
              />
            </div>
            <div>
              <label>Category</label>
              <Select
                value={editingItem.dmr_category_id || ''}
                onChange={val => setEditingItem({ ...editingItem, dmr_category_id: val })}
                options={categories.map(c => ({ value: c.id, label: c.name }))}
              />
            </div>
            <div>
              <label>Subsection</label>
              <Select
                value={editingItem.dmr_subsection_id || ''}
                onChange={val => setEditingItem({ ...editingItem, dmr_subsection_id: val })}
                options={subsections.map(s => ({ value: s.id, label: s.name }))}
              />
            </div>
            <div>
              <label>Material</label>
              <Select
                value={editingItem.material_id || ''}
                onChange={val => setEditingItem({ ...editingItem, material_id: val })}
                options={materials.map(m => ({ value: m.material_id || m.id, label: m.name }))}
              />
            </div>
            <div>
              <label>BOQ Code</label>
              <Input
                value={editingItem.boq_code || ''}
                onChange={val => setEditingItem({ ...editingItem, boq_code: val })}
              />
            </div>
            <div style={{ gridColumn: 'span 2' }}>
              <label>Description</label>
              <Textarea
                value={editingItem.description || ''}
                onChange={val => setEditingItem({ ...editingItem, description: val })}
                rows={3}
              />
            </div>
            <div>
              <label>Unit of Measure</label>
              <Select
                value={editingItem.unit_of_measure || ''}
                onChange={val => setEditingItem({ ...editingItem, unit_of_measure: val })}
                options={[
                  { value: 'PCS', label: 'PCS' },
                  { value: 'MT', label: 'MT' },
                  { value: 'KG', label: 'KG' },
                  { value: 'LS', label: 'LS' },
                  { value: 'Lot', label: 'Lot' },
                  { value: 'M', label: 'M' }
                ]}
              />
            </div>
            <div>
              <label>Budgeted Quantity</label>
              <Input
                type="number"
                value={editingItem.budgeted_quantity || 0}
                onChange={val => setEditingItem({ ...editingItem, budgeted_quantity: val })}
              />
            </div>
            <div>
              <label>Consultant Quantity</label>
              <Input
                type="number"
                value={editingItem.consultant_quantity ?? ''}
                placeholder="Same as budgeted"
                onChange={val => setEditingItem({ ...editingItem, consultant_quantity: val === '' ? null : val })}
              />
            </div>
            <div>
              <label>Basic Rate (Materials)</label>
              <Input
                type="number"
                value={editingItem.basic_rate_material || 0}
                onChange={val => setEditingItem({ ...editingItem, basic_rate_material: val })}
              />
            </div>
            <div>
              <label>Factor</label>
              <Input
                type="number"
                value={editingItem.factor || 0}
                onChange={val => setEditingItem({ ...editingItem, factor: val })}
              />
            </div>
            <div>
              <label>Unit Hour (Labour)</label>
              <Input
                type="number"
                value={editingItem.unit_hour || 0}
                onChange={val => setEditingItem({ ...editingItem, unit_hour: val })}
              />
            </div>
            <div>
              <label>Price Source Amount</label>
              <Input
                type="number"
                value={editingItem.price_source_amount || 0}
                onChange={val => setEditingItem({ ...editingItem, price_source_amount: val })}
              />
            </div>
          </FormGrid>
          <div style={{ marginTop: 24, padding: 16, background: '#f8fafc', borderRadius: 8 }}>
            <p style={{ margin: 0, fontSize: 11.5, color: '#64748b' }}>
              Note: Saving this item will automatically recalculate the final basic and install rates, and total amount, using the pricing parameters originally applied to this item.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 24 }}>
            <Btn variant="ghost" onClick={() => setEditingItem(null)}>Cancel</Btn>
            <Btn variant="primary" onClick={handleSaveItem} disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Btn>
          </div>
        </Modal>
      )}

      {/* Add Component Modal */}
      {addingComponentToItem && (
        <Modal title="Add Component" onClose={() => setAddingComponentToItem(null)} headerBg="#0b318b" headerColor="#ffffff" width={800}>
          <FormGrid cols={2}>
            <div>
              <label>Sub-Component Code</label>
              <Input
                value={newComponentData.sub_component_code}
                onChange={val => setNewComponentData({ ...newComponentData, sub_component_code: val })}
              />
            </div>
            <div>
              <label>Material</label>
              <Select
                value={newComponentData.material_id}
                onChange={val => setNewComponentData({ ...newComponentData, material_id: val })}
                options={materials.map(m => ({ value: m.material_id || m.id, label: m.name }))}
              />
            </div>
            <div style={{ gridColumn: 'span 2' }}>
              <label>Description</label>
              <Textarea
                value={newComponentData.description}
                onChange={val => setNewComponentData({ ...newComponentData, description: val })}
                rows={3}
              />
            </div>
            <div>
              <label>Unit of Measure</label>
              <Select
                value={newComponentData.unit_of_measure}
                onChange={val => setNewComponentData({ ...newComponentData, unit_of_measure: val })}
                options={[
                  { value: 'PCS', label: 'PCS' },
                  { value: 'MT', label: 'MT' },
                  { value: 'KG', label: 'KG' },
                  { value: 'LS', label: 'LS' },
                  { value: 'Lot', label: 'Lot' },
                  { value: 'M', label: 'M' },
                  { value: 'HRS', label: 'HRS' },
                  { value: 'DAYS', label: 'DAYS' }
                ]}
              />
            </div>
            <div>
              <label>Budgeted Quantity</label>
              <Input
                type="number"
                value={newComponentData.budgeted_quantity}
                onChange={val => setNewComponentData({ ...newComponentData, budgeted_quantity: val })}
              />
            </div>
            <div>
              <label>Consultant Quantity</label>
              <Input
                type="number"
                value={newComponentData.consultant_quantity ?? ''}
                placeholder="Same as budgeted"
                onChange={val => setNewComponentData({ ...newComponentData, consultant_quantity: val === '' ? null : val })}
              />
            </div>
            <div>
              <label>Basic Rate (Materials)</label>
              <Input
                type="number"
                value={newComponentData.basic_rate_material}
                onChange={val => setNewComponentData({ ...newComponentData, basic_rate_material: val })}
              />
            </div>
            <div>
              <label>Factor</label>
              <Input
                type="number"
                value={newComponentData.factor}
                onChange={val => setNewComponentData({ ...newComponentData, factor: val })}
              />
            </div>
            <div>
              <label>Unit Hour (Labour)</label>
              <Input
                type="number"
                value={newComponentData.unit_hour}
                onChange={val => setNewComponentData({ ...newComponentData, unit_hour: val })}
              />
            </div>
            <div>
              <label>Price Source Amount</label>
              <Input
                type="number"
                value={newComponentData.price_source_amount}
                onChange={val => setNewComponentData({ ...newComponentData, price_source_amount: val })}
              />
            </div>
          </FormGrid>
          <div style={{ marginTop: 24, padding: 16, background: '#f8fafc', borderRadius: 8 }}>
            <p style={{ margin: 0, fontSize: 11.5, color: '#64748b' }}>
              Note: Component totals will be automatically calculated.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 24 }}>
            <Btn variant="ghost" onClick={() => setAddingComponentToItem(null)}>Cancel</Btn>
            <Btn onClick={handleCreateComponent} disabled={isSaving || !newComponentData.sub_component_code || !newComponentData.description}>
              {isSaving ? 'Adding...' : 'Add Component'}
            </Btn>
          </div>
        </Modal>
      )}

      {/* Edit Component Modal */}
      {editingComponent && (
        <Modal title="Edit Component" onClose={() => setEditingComponent(null)} headerBg="#0b318b" headerColor="#ffffff" width={800}>
          <FormGrid cols={2}>
            <div>
              <label>Sub-Component Code</label>
              <Input
                value={editingComponent.sub_component_code || ''}
                onChange={val => setEditingComponent({ ...editingComponent, sub_component_code: val })}
              />
            </div>
            <div>
              <label>Material</label>
              <Select
                value={editingComponent.material_id || ''}
                onChange={val => setEditingComponent({ ...editingComponent, material_id: val })}
                options={materials.map(m => ({ value: m.material_id || m.id, label: m.name }))}
              />
            </div>
            <div style={{ gridColumn: 'span 2' }}>
              <label>Description</label>
              <Textarea
                value={editingComponent.description || ''}
                onChange={val => setEditingComponent({ ...editingComponent, description: val })}
                rows={3}
              />
            </div>
            <div>
              <label>Unit of Measure</label>
              <Select
                value={editingComponent.unit_of_measure || ''}
                onChange={val => setEditingComponent({ ...editingComponent, unit_of_measure: val })}
                options={[
                  { value: 'PCS', label: 'PCS' },
                  { value: 'MT', label: 'MT' },
                  { value: 'KG', label: 'KG' },
                  { value: 'LS', label: 'LS' },
                  { value: 'Lot', label: 'Lot' },
                  { value: 'M', label: 'M' },
                  { value: 'HRS', label: 'HRS' },
                  { value: 'DAYS', label: 'DAYS' }
                ]}
              />
            </div>
            <div>
              <label>Budgeted Quantity</label>
              <Input
                type="number"
                value={editingComponent.budgeted_quantity || 0}
                onChange={val => setEditingComponent({ ...editingComponent, budgeted_quantity: val })}
              />
            </div>
            <div>
              <label>Consultant Quantity</label>
              <Input
                type="number"
                value={editingComponent.consultant_quantity ?? ''}
                placeholder="Same as budgeted"
                onChange={val => setEditingComponent({ ...editingComponent, consultant_quantity: val === '' ? null : val })}
              />
            </div>
            <div>
              <label>Basic Rate (Materials)</label>
              <Input
                type="number"
                value={editingComponent.basic_rate_material || 0}
                onChange={val => setEditingComponent({ ...editingComponent, basic_rate_material: val })}
              />
            </div>
            <div>
              <label>Factor</label>
              <Input
                type="number"
                value={editingComponent.factor || 0}
                onChange={val => setEditingComponent({ ...editingComponent, factor: val })}
              />
            </div>
            <div>
              <label>Unit Hour (Labour)</label>
              <Input
                type="number"
                value={editingComponent.unit_hour || 0}
                onChange={val => setEditingComponent({ ...editingComponent, unit_hour: val })}
              />
            </div>
            <div>
              <label>Price Source Amount</label>
              <Input
                type="number"
                value={editingComponent.price_source_amount || 0}
                onChange={val => setEditingComponent({ ...editingComponent, price_source_amount: val })}
              />
            </div>
          </FormGrid>
          <div style={{ marginTop: 24, padding: 16, background: '#f8fafc', borderRadius: 8 }}>
            <p style={{ margin: 0, fontSize: 11.5, color: '#64748b' }}>
              Note: Saving this component will automatically recalculate the final basic and install rates, and total amount, using the pricing parameters originally applied to this item.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 24 }}>
            <Btn variant="ghost" onClick={() => setEditingComponent(null)}>Cancel</Btn>
            <Btn variant="primary" onClick={handleSaveComponent} disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Btn>
          </div>
        </Modal>
      )}

      {/* Delete Component Confirmation Modal */}
      {deletingComponent && (
        <Modal title="Delete Component" onClose={() => setDeletingComponent(null)} headerBg="#dc2626" headerColor="#ffffff">
          <div style={{ padding: 24 }}>
            <p style={{ marginBottom: 24, fontSize: 15, color: 'var(--text-primary)', lineHeight: 1.5 }}>
              Are you sure you want to delete this component? <strong>This action cannot be undone</strong>.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <Btn variant="ghost" onClick={() => setDeletingComponent(null)}>Cancel</Btn>
              <Btn variant="danger" onClick={handleDeleteComponent} disabled={isSaving}>
                {isSaving ? 'Deleting...' : 'Delete Component'}
              </Btn>
            </div>
          </div>
        </Modal>
      )}


      {/* Delete Confirmation Modal */}
      {addingBuilding && (
        <Modal title="Add Building / Zone" onClose={() => setAddingBuilding(false)} headerBg="#0b318b" headerColor="#ffffff" width={480}>
          {freeDivisions.length ? (
            <Select
              label="Building / Zone"
              value={newBuildingId}
              onChange={setNewBuildingId}
              options={[{ value: '', label: 'Select a building...' }, ...freeDivisions.map(d => ({ value: d.project_division_id || d.id, label: d.name }))]}
            />
          ) : (
            <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Every building/zone of this project is already a tab. Add more in Project Setup.</p>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
            <Btn variant="ghost" onClick={() => setAddingBuilding(false)}>Cancel</Btn>
            <Btn onClick={handleAddBuilding} loading={isSaving} disabled={!newBuildingId}>Add Tab</Btn>
          </div>
        </Modal>
      )}

      {removingBuilding && (
        <Modal title="Remove Building Tab" onClose={() => setRemovingBuilding(null)} headerBg="#ef4444" headerColor="#ffffff" width={480}>
          <p style={{ fontSize: 14 }}>
            Remove <strong>{buildingName(removingBuilding)}</strong> from this BOQ? A building that still has lines can't be removed — move or delete its lines first.
          </p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
            <Btn variant="ghost" onClick={() => setRemovingBuilding(null)}>Cancel</Btn>
            <Btn variant="danger" onClick={handleRemoveBuilding} loading={isSaving}>Remove</Btn>
          </div>
        </Modal>
      )}

      {showDeleteModal && (
        <Modal title="Delete BOQ Form" onClose={() => setShowDeleteModal(false)} headerBg="#0b318bff" headerColor="#ffffff">
          <div style={{ padding: 24 }}>
            <p style={{ marginBottom: 24, fontSize: 15, color: 'var(--text-primary)', lineHeight: 1.5 }}>
              Are you sure you want to delete this Bill of Quantities? <strong>This action cannot be undone</strong> and will permanently delete the BOQ along with all its line items.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <Btn onClick={() => setShowDeleteModal(false)} disabled={isDeleting} style={{ background: 'var(--red)', color: 'white', border: 'none' }}>Cancel</Btn>
              <Btn onClick={handleDeleteBoq} disabled={isDeleting} style={{ background: '#0b318bff', color: 'white', border: 'none' }}>
                {isDeleting ? 'Deleting...' : 'Yes, Delete BOQ'}
              </Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}


function BOQBudgetsTab({ projectId, toast }) {
  const [budgets, setBudgets] = useState([]);
  const [recon, setRecon] = useState([]);
  const [total, setTotal] = useState(null);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState('budgets');

  const load = async () => {
    setLoading(true);
    try {
      const [b, t, r, c] = await Promise.all([
        api.listBOQBudgets(projectId),
        api.getBOQTotal(projectId),
        api.getBOQReconciliation(projectId),
        api.listCategories(),
      ]);
      setBudgets(Array.isArray(b) ? b : []);
      setTotal(t);
      setRecon(Array.isArray(r) ? r : []);
      setCats(Array.isArray(c) ? c : []);
    } catch { }
    setLoading(false);
  };
  useEffect(() => { load(); }, [projectId]);

  const catOpts = [{ value: '', label: '— Select Category —' }, ...cats.map(c => ({ value: c.cost_category_id, label: `${c.code} – ${c.name}` }))];

  return (
    <div className="fade-in" style={{ marginTop: 20 }}>
      <div className="grid-cols-3" style={{ gap: 14, marginBottom: 14, width: '100%' }}>
        <StatCard label="Total Budgets" value={budgets.length} color="cyan" />
        <StatCard label="Total Original Budget" value={`₦${parseFloat(total?.total_boq_amount || 0).toLocaleString()}`} color="accent" />
        <StatCard label="Reconciliation Issues" value={recon.filter(r => parseFloat(r.variance || 0) < 0).length} color="red" />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
          {/* Total BOQ moved to stat cards */}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setView('budgets')} style={{ padding: '5px 12px', borderRadius: 6, fontSize: 11, fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--font)', border: '1px solid var(--border)', background: view === 'budgets' ? 'var(--accent)' : 'var(--bg-elevated)', color: view === 'budgets' ? '#fff' : 'var(--text-secondary)' }}>Budgets</button>
          <button onClick={() => setView('recon')} style={{ padding: '5px 12px', borderRadius: 6, fontSize: 11, fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--font)', border: '1px solid var(--border)', background: view === 'recon' ? 'var(--accent)' : 'var(--bg-elevated)', color: view === 'recon' ? '#fff' : 'var(--text-secondary)' }}>Reconciliation</button>
        </div>
      </div>

      {view === 'budgets' && (
        <Card>
          <Table loading={loading} cols={[
            { key: 'category_id', align: 'center', label: 'Category', render: v => { const c = cats.find(x => x.cost_category_id === v); return c ? <span>{c.code} – {c.name}</span> : <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-muted)' }}>{v?.slice(0, 12)}</span>; } },
            { key: 'original_budget', align: 'center', label: 'Original Budget', render: v => <span style={{ fontFamily: 'var(--mono)', color: 'var(--accent)', fontWeight: 700 }}>₦{parseFloat(v || 0).toLocaleString()}</span> },
            { key: 'revised_budget', align: 'center', label: 'Revised Budget', render: v => v ? <span style={{ fontFamily: 'var(--mono)', color: 'var(--amber)' }}>₦{parseFloat(v || 0).toLocaleString()}</span> : '—' },
            { key: 'notes', align: 'center', label: 'Notes', render: v => <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{v || '—'}</span> },
          ]} rows={budgets} />
        </Card>
      )}

      {view === 'recon' && (
        <Card>
          <Table loading={loading} cols={[
            { key: 'category_name', align: 'center', label: 'Category' },
            { key: 'category_code', align: 'center', label: 'Code', render: v => <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--accent)' }}>{v}</span> },
            { key: 'budget_envelope', align: 'center', label: 'Budget', render: v => <span style={{ fontFamily: 'var(--mono)' }}>₦{parseFloat(v || 0).toLocaleString()}</span> },
            { key: 'boq_items_total', align: 'center', label: 'BOQ Total', render: v => <span style={{ fontFamily: 'var(--mono)' }}>₦{parseFloat(v || 0).toLocaleString()}</span> },
            { key: 'variance', align: 'center', label: 'Variance', render: v => { const n = parseFloat(v || 0); return <span style={{ fontFamily: 'var(--mono)', fontWeight: 700, color: n >= 0 ? 'var(--green)' : 'var(--red)' }}>₦{n.toLocaleString()}</span>; } },
            { key: 'is_leaf', align: 'center', label: 'Type', render: v => <Badge color={v ? 'cyan' : 'gray'}>{v ? 'Leaf' : 'Parent'}</Badge> },
          ]} rows={recon} />
        </Card>
      )}

      {modal && (
        <Modal title="New BOQ Budget" onClose={() => setModal(false)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Select label="Category" required value={form.category_id || ''} onChange={v => setForm(f => ({ ...f, category_id: v }))} options={catOpts} />
            <FormGrid>
              <Input label="Original Budget" required type="number" value={form.original_budget || ''} onChange={v => setForm(f => ({ ...f, original_budget: v }))} />
              <Input label="Revised Budget" type="number" value={form.revised_budget || ''} onChange={v => setForm(f => ({ ...f, revised_budget: v }))} />
            </FormGrid>
            <Textarea label="Notes" value={form.notes || ''} onChange={v => setForm(f => ({ ...f, notes: v }))} rows={2} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Btn variant="ghost" onClick={() => setModal(false)}>Cancel</Btn>
              <Btn loading={saving} onClick={async () => {
                setSaving(true);
                try { await api.createBOQBudget(projectId, form); toast.show('Budget created'); setModal(false); load(); }
                catch (e) { toast.show(e.message, 'error'); }
                setSaving(false);
              }}>Create</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function BOQHubTab({ projectId, toast }) {
  const [divisions, setDivisions] = useState([]);
  const [selectedDivision, setSelectedDivision] = useState(null);
  const [boqs, setBoqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBoqId, setSelectedBoqId] = useState(null);
  const [isCreating, setIsCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [divs, boqsList] = await Promise.all([
        api.listProjectDivisions(projectId).catch(() => []),
        api.listProjectBOQs(projectId).catch(() => [])
      ]);
      setDivisions(Array.isArray(divs) ? divs : divs?.data || []);
      setBoqs(Array.isArray(boqsList) ? boqsList : boqsList?.data || []);
    } catch(e) {}
    setLoading(false);
  }

  useEffect(() => { load() }, [projectId]);

  if (selectedBoqId) {
    return <BOQItemsTab projectId={projectId} boqId={selectedBoqId} onBack={() => { setSelectedBoqId(null); load(); }} toast={toast} />;
  }

  if (isCreating) {
    return (
      <div className="fade-in" style={{ marginTop: 20 }}>
        <BOQFormPage project={{ project_id: projectId }} onBack={() => { setIsCreating(false); load(); }} toast={toast} />
      </div>
    );
  }

  if (!selectedDivision) {
    return (
      <div className="fade-in" style={{ marginTop: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ margin: 0, color: 'var(--text-primary)' }}>Project Divisions</h3>
        </div>
        
        {loading ? (
          <RNLLoader title="Loading Divisions..." subtitle="Please wait" />
        ) : divisions.length === 0 ? (
          <Card style={{ padding: 60, textAlign: 'center', background: 'var(--bg-elevated)' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>📂</div>
            <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8, color: 'var(--text-primary)' }}>No Divisions Found</h3>
            <p style={{ color: 'var(--text-muted)', marginBottom: 24, maxWidth: 400, margin: '0 auto' }}>
              Please create project divisions in the project settings before managing BOQs.
            </p>
          </Card>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
            {divisions.map(div => {
              const divId = div.project_division_id || div.id;
              const divBoqs = boqs.filter(b => b.project_division_id === divId);
              return (
                <Card 
                  key={divId} 
                  onClick={() => setSelectedDivision(divId)}
                  style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12, border: '2px solid transparent', transition: 'all 0.2s' }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent)'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = 'transparent'}
                >
                  <div style={{ fontSize: 24 }}>📂</div>
                  <h4 style={{ margin: 0, fontSize: 16, color: 'var(--text-primary)', fontWeight: 700 }}>{div.name}</h4>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)', display: 'flex', gap: 8, alignItems: 'center' }}>
                    <Badge color="blue">{divBoqs.length} BOQs</Badge>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  const divisionName = divisions.find(d => (d.project_division_id || d.id) === selectedDivision)?.name || 'Division';
  const filteredBoqs = boqs.filter(b => b.project_division_id === selectedDivision).map(b => {
    const isActive = b.status === 'active';
    return {
      ...b,
      _bgColor: isActive ? '#dcfce7' : '#fee2e2',
      _hoverBgColor: isActive ? '#bbf7d0' : '#fecaca'
    };
  });

  return (
    <div className="fade-in" style={{ marginTop: 20 }}>
       <style>{'#global-project-back-bar { display: none !important; }'}</style>
       <div className="flex-mobile-col" style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: 'var(--bg-card)', border: '1px solid black', borderRadius: 14, boxShadow: 'var(--shadow-sm)', backgroundColor: '#e4ebefdf' }}>
         <button
           onClick={() => setSelectedDivision(null)}
           style={{
             display: 'inline-flex', alignItems: 'center', gap: 6,
             background: 'var(--accent-dim)', color: 'var(--accent)', border: '1px solid color-mix(in srgb, var(--accent) 22%, transparent)', borderRadius: 10,
             padding: '7px 13px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font)'
           }}
         >
           <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></svg>
           Back to Divisions
         </button>
         <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
           Currently managing division: <strong style={{ color: 'var(--text-primary)' }}>{divisionName}</strong>
         </span>
       </div>

       <Card style={{ padding: 20 }}>
         <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
           <div>
             <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: 20, fontWeight: 700 }}>{divisionName}</h3>
           </div>
           <Btn onClick={() => setIsCreating(true)}>Create New BOQ Form</Btn>
         </div>
         <Table 
           loading={loading}
           cols={[
             { key: 'name', label: 'BOQ Name' },
             { key: 'description', label: 'Description', render: v => <span style={{ color: 'var(--text-muted)' }}>{v || '—'}</span> },
             { key: 'total_amount', label: 'Amount', render: v => <span style={{ fontFamily: 'var(--mono)', fontWeight: 600 }}>₦{parseFloat(v||0).toLocaleString()}</span> },
             { key: 'status', label: 'Status', render: v => {
                const isActive = v === 'active';
                return <span style={{ color: isActive ? 'var(--green)' : 'var(--red)', fontWeight: 600, background: isActive ? '#bbf7d0' : '#fecaca', padding: '4px 8px', borderRadius: 12, fontSize: 11 }}>{v?.toUpperCase()}</span>;
             }},
             { key: 'actions', label: '', align: 'right', render: (_, row) => (
                <Btn variant="primary" style={{background: '#ffffff', color: '#0b318bff'}} onClick={() => setSelectedBoqId(row.id)}>View Form</Btn>
             )}
           ]}
           rows={filteredBoqs}
         />
       </Card>
    </div>
  )
}

export default function BOQPage({ toast }) {
  const { projectId, tab } = useParams();

  if (!projectId) return <NeedProject />;

  return (
    <div className="fade-in">
      {/* Back button removed because App.js renders it globally */}
      <SectionHeader title="Bill of Quantities" subtitle={tab === 'boqbudgets' ? 'BOQ Budgets' : null} />
      {(!tab || tab === 'items') && <BOQHubTab projectId={projectId} toast={toast} />}
      {tab === 'boqbudgets' && <BOQBudgetsTab projectId={projectId} toast={toast} />}
    </div>
  );
}
